import React, {useState} from 'react';
import _ from 'underscore';

import {TagSchema} from 'constants/RegistryConstants';
import {FileLinkSchemaTag, isSchemaTag} from 'folder-items/FileConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import {ListableItem} from 'search/SearchConstants';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import {
  BulkUnlinkSpec,
  CategoricalUnlinkSpec,
  PartsOrPartLinks,
  SchemaFieldInfoById,
  SequencesById,
  TagSpecForSequence,
} from './BulkUnlinkPartsConstants';
import BulkUnlinkPartsDropdown from './BulkUnlinkPartsDropdown';
import BulkUnlinkPartsTable from './BulkUnlinkPartsTable';

interface PartsBySequenceId {
  [sequenceId: string]: Array<string>;
}

interface UnlinkSpec {
  sequenceId: string;
  partSequenceIds: Array<string>;
}

interface BulkUnlinkOptions {
  unlinkSpecs: Array<UnlinkSpec>;
}

export interface BulkUnlinkPartsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (result: BulkUnlinkOptions) => void;
  sequenceToParts: PartsBySequenceId;
  sequences: Array<ListableItem>;
  validationExtrasBySequenceId: {
    [id: string]: ValidationExtras;
  };
  tagSchema: TagSchema;
}

function categorizeParts(tagSpecs: Array<TagSpecForSequence>): Array<PartsOrPartLinks> {
  const partsOrPartLinksToRemove = [];
  for (const tagSpec of tagSpecs) {
    if (tagSpec.partSequenceIds.length > 0) {
      partsOrPartLinksToRemove.push({
        partSequenceIds: tagSpec.partSequenceIds,
        containerSequenceId: tagSpec.referringSequenceId,
        validationError: tagSpec.validationError,
      });
    }
  }
  return partsOrPartLinksToRemove;
}

export default function BulkUnlinkPartsModal({
  onRequestClose,
  onRequestSubmit,
  sequenceToParts,
  sequences,
  validationExtrasBySequenceId,
  tagSchema,
}: BulkUnlinkPartsModalProps): JSX.Element {
  const [selectedField, setSelectedField] = useState(CategoricalUnlinkSpec.ALL as BulkUnlinkSpec);
  const [invalidOnly, setInvalidOnly] = useState(true);

  // This is used to render the table and compute the unlink specs; each entry in the array corresponds to a
  // table row
  const partsOrPartLinksToRemove: Array<PartsOrPartLinks> = [];
  // These two are used to compute partsOrPartLinksToRemove and mostly just reshape the data into a format
  // that's easier to work with
  const sequencesById: SequencesById = {};
  const schemaFieldInfoById: SchemaFieldInfoById = {};
  for (const tagSchemaField of tagSchema.fields) {
    if (tagSchemaField.fieldType === FieldType.PART_LINK) {
      schemaFieldInfoById[tagSchemaField.apiId] = {
        schemaFieldName: tagSchemaField.name,
        tagSpecs: [],
      };
    }
  }
  function getErrorForTag(containingSequenceId: string, schemaFieldId: string): string | null {
    for (const sequenceId of Object.keys(validationExtrasBySequenceId)) {
      if (!validationExtrasBySequenceId[sequenceId].errorRecords) {
        continue;
      }
      for (const error of validationExtrasBySequenceId[sequenceId].errorRecords) {
        if (!error.field) {
          continue;
        }
        if (sequenceId === containingSequenceId && schemaFieldId === error.field.apiId) {
          return error.message;
        }
      }
    }
    return null;
  }
  for (const sequence of sequences) {
    if (!sequence.tags) {
      continue;
    }
    for (const tag of sequence.tags) {
      if (
        tag &&
        isSchemaTag(tag) &&
        Object.keys(schemaFieldInfoById).includes(tag.tagSchemaField.apiId) &&
        tag.tagSchemaField.fieldType === FieldType.PART_LINK
      ) {
        schemaFieldInfoById[tag.tagSchemaField.apiId].tagSpecs.push({
          referringSequenceId: sequence.id,
          schemaTagId: tag.tagSchemaField.apiId,
          partSequenceIds: (tag as FileLinkSchemaTag).targetFiles.map((targetFile) => targetFile.id),
          validationError: getErrorForTag(sequence.id, tag.tagSchemaField.apiId),
        });
      }
    }
  }
  for (const sequence of sequences) {
    sequencesById[sequence.id] = sequence;
  }

  if (selectedField === CategoricalUnlinkSpec.ALL) {
    for (const schemaFieldId of Object.keys(schemaFieldInfoById)) {
      partsOrPartLinksToRemove.push(...categorizeParts(schemaFieldInfoById[schemaFieldId].tagSpecs));
    }
  } else if (selectedField === CategoricalUnlinkSpec.ORPHANED) {
    for (const sequenceId of Object.keys(sequenceToParts)) {
      // eslint-disable-next-line no-restricted-syntax
      const referencedPartSequenceIds = _.flatten(
        Object.values(schemaFieldInfoById).map((schemaFieldInfo) =>
          // eslint-disable-next-line no-restricted-syntax
          _.flatten(
            schemaFieldInfo.tagSpecs.filter((tagSpec) => tagSpec.referringSequenceId === sequenceId)
          ).map((tagSpec) => tagSpec.partSequenceIds)
        )
      );
      partsOrPartLinksToRemove.push(
        ...sequenceToParts[sequenceId]
          .filter((partSequenceId) => !referencedPartSequenceIds.includes(partSequenceId))
          .map((partSequenceId) => ({
            partSequenceIds: [partSequenceId],
            containerSequenceId: sequenceId,
            validationError: null,
          }))
      );
    }
  } else {
    partsOrPartLinksToRemove.push(...categorizeParts(schemaFieldInfoById[selectedField].tagSpecs));
  }
  return (
    <Modal
      buttons={[{text: 'Remove', submit: true, disabled: false}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        const unlinkSpecs = [];
        for (const partOrPartLink of partsOrPartLinksToRemove) {
          if (
            partOrPartLink.validationError ||
            !invalidOnly ||
            selectedField === CategoricalUnlinkSpec.ORPHANED
          ) {
            unlinkSpecs.push({
              sequenceId: partOrPartLink.containerSequenceId,
              partSequenceIds: partOrPartLink.partSequenceIds,
            });
          }
        }
        onRequestSubmit({
          unlinkSpecs: unlinkSpecs.filter((unlinkSpec) => unlinkSpec.partSequenceIds.length > 0),
        });
      }}
      title="Remove Parts and Part Links"
    >
      <p>
        {selectedField !== CategoricalUnlinkSpec.ORPHANED
          ? 'Part links will be removed from the selected fields, and the corresponding parts will be removed from the sequence.'
          : 'Selected parts will be removed from the sequence.'}
      </p>
      <FieldLabel label="Select a part link schema field" />
      <BulkUnlinkPartsDropdown
        selectedField={selectedField}
        setSelectedField={setSelectedField}
        schemaFieldInfoById={schemaFieldInfoById}
      />
      <br />
      {selectedField !== CategoricalUnlinkSpec.ORPHANED && (
        <CheckboxInput
          id="invalidLinks"
          label="Only apply to schema fields with validation errors"
          checked={invalidOnly}
          onChange={(e) => {
            setInvalidOnly(e.target.checked);
          }}
          disabled={selectedField === CategoricalUnlinkSpec.ORPHANED}
        />
      )}
      <br />
      <BulkUnlinkPartsTable
        partsOrPartLinksToRemove={partsOrPartLinksToRemove}
        selectedField={selectedField}
        sequencesById={sequencesById}
        validationExtrasBySequenceId={validationExtrasBySequenceId}
      />
    </Modal>
  );
}
